/**
 * The canonical form, and the round trip that defines it.
 *
 * Split out of `index.ts` so the GitBook importer can canonicalise its output
 * without importing the package's own entry point, which would be a cycle.
 */
import { parseDocument, serializeDocument } from './pipeline.js';
import { mdastToDoc, docToMdast } from './convert.js';
/** Markdown -> the editor's document model. */
export function markdownToProseMirror(markdown) {
    const parsed = parseDocument(markdown);
    return {
        frontmatter: parsed.frontmatter,
        doc: mdastToDoc(parsed.body),
        ...(parsed.frontmatterText ? { frontmatterText: parsed.frontmatterText } : {}),
    };
}
/** The editor's document model -> Markdown. */
export function proseMirrorToMarkdown(frontmatter, doc, 
/**
 * The frontmatter this document was parsed from, when there was one.
 *
 * Optional, and absent for a document somebody BUILT rather than parsed —
 * an importer, a generator — which has no previous text and nothing to keep.
 */
frontmatterText) {
    return serializeDocument({
        frontmatter,
        body: docToMdast(doc),
        ...(frontmatterText ? { frontmatterText } : {}),
    });
}
/** One Markdown -> ProseMirror -> Markdown transformation. */
function transform(markdown) {
    const { frontmatter, doc, frontmatterText } = markdownToProseMirror(markdown);
    return proseMirrorToMarkdown(frontmatter, doc, frontmatterText);
}
/**
 * A single transformation is not always a fixed point, so iterate to one.
 *
 * Three passes is the deepest convergence observed by the property test. The
 * cause is Markdown escaping, not our converters: text containing literal `~`
 * next to strikethrough delimiters re-escapes differently on a second pass
 * (`~~\~\~~~...` reparses as something new). Pathological, but a writer typing
 * `~~about ~5~~` can reach it.
 *
 * The bound matters more than the number. Without it a non-converging input
 * would churn the file on every single save, producing an endless stream of
 * meaningless diffs that bury real changes in review. With it, such an input is
 * a loud error we can fix, which is the failure mode we want.
 */
const MAX_CANONICALISE_PASSES = 6;
/**
 * Normalise to the canonical on-disk form.
 *
 * The canonical form is DEFINED as what the editor model produces, so this is a
 * full Markdown -> ProseMirror -> Markdown pass rather than a Markdown-only
 * reformat. Every write goes through it, which is what makes saving an unchanged
 * file a byte-identical no-op instead of a noise diff.
 *
 * Going through the editor model applies two normalisations that a Markdown-only
 * pass cannot. Both are safe because the rendered output is identical either way,
 * and both were found by the property test rather than by reading the code:
 *
 * 1. **Nested identical emphasis collapses.** `*_a_*` is emphasis inside
 *    emphasis, which Markdown cannot express unambiguously — remark-stringify
 *    emits `__a__`, and that reparses as STRONG. Left alone, a writer's nested
 *    italics silently became bold. Now it collapses to `_a_`.
 *
 * 2. **Mark nesting order is canonicalised**, link outermost. `_[x](u)_` and
 *    `[_x_](u)` render identically and are the same document in ProseMirror,
 *    whose marks are a flat set. Picking one spelling makes the round trip a
 *    fixed point instead of alternating between the two forever.
 */
export function canonicalise(markdown) {
    let current = transform(markdown);
    for (let pass = 1; pass < MAX_CANONICALISE_PASSES; pass++) {
        const next = transform(current);
        if (next === current)
            return current;
        current = next;
    }
    if (transform(current) !== current) {
        throw new Error(`canonicalise did not converge in ${MAX_CANONICALISE_PASSES} passes. ` +
            'This is a formatting bug, not a content problem — report the file.');
    }
    return current;
}
/**
 * Markdown-only reformat: no editor model, so it preserves mark nesting exactly.
 * Useful for diffing "is this just a formatting change?", but it is NOT the
 * canonical form — see `canonicalise`.
 */
export function reformat(markdown) {
    return serializeDocument(parseDocument(markdown));
}
//# sourceMappingURL=canonical.js.map